// frontend/src/components/common/FAQ.jsx
// Frequently Asked Questions Component
// ========================================================
// Created: February 24, 2026 - UX2 Phase 2
// Common questions: What are tokens? How is it shipped? How do refunds work?
// ========================================================

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Coins, Truck, RefreshCw, Users, Shield, Clock } from 'lucide-react';

const FAQItem = ({ question, answer, icon: Icon, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 px-1 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon size={18} className="text-blue-600 flex-shrink-0" />}
          <span className="font-medium text-gray-900">{question}</span>
        </div>
        {isOpen ? (
          <ChevronUp size={18} className="text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
        )}
      </button>
      
      {isOpen && (
        <div className="pb-4 px-1 pl-10 text-gray-600 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQ = ({ 
  showTitle = true, 
  maxItems = null, 
  className = '',
  compact = false 
}) => {
  const [openIndex, setOpenIndex] = useState(0); // First item open by default

  const faqs = [
    {
      question: "What are tokens and how do I use them?",
      icon: Coins,
      answer: (
        <>
          <p className="mb-2">
            Tokens are UniLedge's reward currency. <strong>10 tokens = $1.00</strong> in savings at checkout.
          </p>
          <p className="mb-2">
            <strong>Earn tokens by:</strong>
          </p>
          <ul className="list-disc pl-5 mb-2 space-y-1">
            <li>Making purchases (~10 tokens per item)</li>
            <li>Referring friends (25 tokens + 10% of their earnings for 90 days)</li>
            <li>Completing community jobs (15-50 tokens)</li>
            <li>Reaching milestones and achievements</li>
            <li>Choosing pickup instead of delivery (bonus tokens)</li>
          </ul>
          <p>
            <strong>Redeem tokens</strong> at checkout for up to 40% off your order, based on your loyalty tier.
          </p>
        </>
      )
    },
    {
      question: "How does shipping and delivery work?",
      icon: Truck,
      answer: (
        <>
          <p className="mb-2">
            UniLedge offers two fulfillment options:
          </p>
          <ul className="list-disc pl-5 mb-2 space-y-1">
            <li><strong>Delivery ($5.99):</strong> Products shipped directly to your address</li>
            <li><strong>Pickup (Free + Bonus Tokens):</strong> Collect from a local pickup point and earn extra tokens</li>
          </ul>
          <p className="mb-2">
            Orders are fulfilled once the group buying window closes and the minimum buyer threshold is met. 
            You'll receive email updates at each stage: confirmed, processing, shipped, and delivered.
          </p>
          <p>
            Typical delivery takes 3-7 business days depending on your location and the vendor.
          </p>
        </>
      )
    },
    {
      question: "How do refunds and returns work?",
      icon: RefreshCw,
      answer: (
        <>
          <p className="mb-2">
            Our refund policy depends on the order status:
          </p>
          <ul className="list-disc pl-5 mb-2 space-y-1">
            <li><strong>Before group threshold met:</strong> Full refund, no questions asked</li>
            <li><strong>After threshold, before shipping:</strong> Refund minus up to 15% restocking fee</li>
            <li><strong>After shipping:</strong> Subject to vendor's return policy (usually 7-14 days)</li>
            <li><strong>Defective or wrong items:</strong> Full refund or replacement, always</li>
          </ul>
          <p>
            To request a refund, go to your order history and select "Request Refund" or contact support. 
            Refunds are processed within 5-10 business days to your original payment method.
          </p>
        </>
      )
    },
    {
      question: "How does group buying save me money?",
      icon: Users,
      answer: (
        <>
          <p className="mb-2">
            When you buy alone, you pay retail prices. When you buy together with your neighbors, 
            you unlock <strong>wholesale pricing</strong> that stores can't match.
          </p>
          <p className="mb-2">
            <strong>Our pricing tiers:</strong>
          </p>
          <ul className="list-disc pl-5 mb-2 space-y-1">
            <li>5+ buyers: 8-10% off retail</li>
            <li>10+ buyers: 16-19% off retail</li>
            <li>25+ buyers: 23-30% off retail</li>
            <li>50+ buyers: 35-40% off retail</li>
          </ul>
          <p>
            UniLedge automatically groups you with nearby buyers using location technology. 
            You don't need to coordinate with anyone — just shop, and savings happen automatically.
          </p>
        </>
      )
    },
    {
      question: "Is my payment secure?",
      icon: Shield,
      answer: (
        <>
          <p className="mb-2">
            Yes! UniLedge uses <strong>Stripe</strong>, a PCI DSS Level 1 certified payment processor — 
            the same security standard used by Amazon, Google, and major banks.
          </p>
          <p className="mb-2">
            We never store your complete credit card number. All transactions are encrypted 
            and your payment info goes directly to Stripe's secure servers.
          </p>
          <p>
            Your funds are held in escrow until the group buying window closes. If the minimum 
            threshold isn't reached, you're automatically refunded in full.
          </p>
        </>
      )
    },
    {
      question: "What happens if a group doesn't reach its minimum?",
      icon: Clock,
      answer: (
        <>
          <p className="mb-2">
            If a group doesn't reach the minimum number of buyers before the window closes:
          </p>
          <ul className="list-disc pl-5 mb-2 space-y-1">
            <li>Your payment authorization is automatically cancelled</li>
            <li>No charges are made to your card</li>
            <li>You'll receive an email notification</li>
            <li>You can join another group or browse other deals</li>
          </ul>
          <p>
            This is rare — most groups reach their threshold! You can see real-time progress 
            on each product page showing how many buyers have joined.
          </p>
        </>
      )
    }
  ];

  const displayFaqs = maxItems ? faqs.slice(0, maxItems) : faqs;

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div className={className}>
      {showTitle && (
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle size={20} className="text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h3>
        </div>
      )}
      
      <div className={`bg-white rounded-xl ${compact ? '' : 'shadow-sm border border-gray-200 p-4'}`}>
        {displayFaqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            icon={faq.icon}
            isOpen={openIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default FAQ;